/**
 * Global Excel Configurator - Data Source Types
 *
 * Defines the adapter interface for connecting different data sources
 * to the excel configurator system.
 */

import type { ColumnConfig, ExcelConfiguration, ValidationResult } from "./configuration";

// ============================================================================
// Data Source Types
// ============================================================================

/**
 * Field metadata for data source
 */
export interface FieldMetadata {
  /** Field key/identifier */
  key: string;
  /** Display label */
  label: string;
  /** Field data type */
  type: "text" | "number" | "date" | "boolean";
  /** Field description */
  description?: string;
  /** Whether this field is required */
  required?: boolean;
  /** Example value */
  example?: string;
}

/**
 * Row data for Excel generation
 */
export type RowData = Record<string, any>;

// ============================================================================
// Data Source Adapter Interface
// ============================================================================

/**
 * Data source adapter interface
 *
 * @template TSource - Type of the source entity (e.g., Shipment, Order)
 * @template TRow - Type of the extracted row data
 */
export interface DataSourceAdapter<TSource = any, TRow extends RowData = RowData> {
  /** Unique identifier for this data source type */
  readonly type: string;

  /** Human-readable name for this data source */
  readonly name: string;

  /** Description of this data source */
  readonly description: string;

  /**
   * Get available fields from this data source
   */
  getAvailableFields(): FieldMetadata[];

  /**
   * Extract rows from source entity
   *
   * @param source - Source entity
   * @returns Array of row data
   */
  extractRows(source: TSource): TRow[];

  /**
   * Get default column configuration for this data source
   */
  getDefaultConfiguration(): ExcelConfiguration<TSource>;

  /**
   * Validate a configuration for this data source
   *
   * @param configuration - Configuration to validate
   * @returns Validation result
   */
  validateConfiguration(configuration: ExcelConfiguration<TSource>): ValidationResult;

  /**
   * Transform a row using the column configuration
   *
   * @param row - Source row data
   * @param columns - Column configuration
   * @returns Transformed row data
   */
  transformRow(row: TRow, columns: ColumnConfig[]): Record<string, any>;

  /**
   * Evaluate a formula for a specific row
   *
   * @param formula - Formula expression
   * @param row - Row data
   * @returns Evaluated result
   */
  evaluateFormula?(formula: string, row: TRow): any;
}

// ============================================================================
// Base Adapter Abstract Class
// ============================================================================

/**
 * Base data source adapter with common functionality
 *
 * @template TSource - Type of the source entity
 * @template TRow - Type of the extracted row data
 */
export abstract class BaseDataSourceAdapter<TSource = any, TRow extends RowData = RowData>
  implements DataSourceAdapter<TSource, TRow>
{
  abstract readonly type: string;
  abstract readonly name: string;
  abstract readonly description: string;

  abstract getAvailableFields(): FieldMetadata[];
  abstract extractRows(source: TSource): TRow[];
  abstract getDefaultConfiguration(): ExcelConfiguration<TSource>;

  /**
   * Default validation implementation
   */
  validateConfiguration(configuration: ExcelConfiguration<TSource>): ValidationResult {
    const errors: ValidationResult["errors"] = [];
    const warnings: ValidationResult["warnings"] = [];

    // Validate configuration has name
    if (!configuration.name?.trim()) {
      errors.push({
        type: "missing_field",
        message: "Configuration name is required",
      });
    }

    // Validate at least one column
    if (!configuration.columns || configuration.columns.length === 0) {
      errors.push({
        type: "missing_field",
        message: "At least one column is required",
      });
    }

    // Get available field keys
    const availableFields = this.getAvailableFields().map((f) => f.key);

    // Validate each column
    configuration.columns?.forEach((column) => {
      // Check required columns have field mapping
      if (column.required && !column.fieldMapping) {
        errors.push({
          type: "missing_field",
          message: `Required column "${column.label}" must have a field mapping`,
          columnId: column.id,
        });
      }

      // Check field mapping exists in available fields
      if (column.fieldMapping && !availableFields.includes(column.fieldMapping)) {
        warnings.push({
          type: "unmapped_column",
          message: `Field "${column.fieldMapping}" not found in available fields`,
          columnId: column.id,
        });
      }

      // Width checking removed - not part of ColumnConfig type

      // Validate formula columns have formulas
      if (column.type === "formula" && !column.formula) {
        errors.push({
          type: "invalid_formula",
          message: `Formula column "${column.label}" must have a formula`,
          columnId: column.id,
        });
      }
    });

    // Check for duplicate column IDs
    const columnIds = configuration.columns?.map((c) => c.id) || [];
    const duplicates = columnIds.filter((id, index) => columnIds.indexOf(id) !== index);
    if (duplicates.length > 0) {
      errors.push({
        type: "duplicate_column",
        message: `Duplicate column IDs found: ${duplicates.join(", ")}`,
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * Default row transformation implementation
   */
  transformRow(row: TRow, columns: ColumnConfig[]): Record<string, any> {
    const transformed: Record<string, any> = {};

    columns.forEach((column) => {
      if (!column.visible) return;

      const key = column.label;

      // Handle formula columns
      if (column.type === "formula" && column.formula) {
        transformed[key] = this.evaluateFormula
          ? this.evaluateFormula(column.formula, row)
          : column.formula;
        return;
      }

      // Handle fixed value columns (manual input)
      if (column.fixedValue !== undefined && column.fixedValue !== null) {
        transformed[key] = column.fixedValue;
        return;
      }

      // Handle mapped fields
      if (column.fieldMapping) {
        const value = row[column.fieldMapping];

        // Type-specific transformations
        switch (column.type) {
          case "number":
            transformed[key] = typeof value === "number" ? value : parseFloat(value) || 0;
            break;
          case "boolean":
            transformed[key] = Boolean(value);
            break;
          case "date":
            transformed[key] = value instanceof Date ? value : new Date(value);
            break;
          default:
            transformed[key] = value ?? "";
        }
      } else {
        transformed[key] = "";
      }
    });

    return transformed;
  }

  /**
   * Basic formula evaluation (override for advanced functionality)
   * Uses a safe expression evaluator instead of eval()
   */
  evaluateFormula(formula: string, row: TRow): any {
    // Basic field reference replacement: {fieldName} -> row[fieldName]
    let result = formula;

    Object.keys(row).forEach((key) => {
      const value = row[key];
      const placeholder = `{${key}}`;

      if (result.includes(placeholder)) {
        // Replace with actual value
        result = result.replace(
          new RegExp(`\\{${key}\\}`, "g"),
          typeof value === "number" ? String(value) : `"${value}"`
        );
      }
    });

    // Safe evaluation using Function constructor with strict mode
    // This is safer than eval() as it doesn't have access to the local scope
    try {
      return this.safeEvaluateExpression(result);
    } catch {
      return result;
    }
  }

  /**
   * Safely evaluate a mathematical or simple expression
   * More secure than eval() as it runs in isolated scope
   */
  private safeEvaluateExpression(expression: string): any {
    // Whitelist allowed characters: numbers, operators, parentheses, dots, quotes, spaces
    const allowedPattern = /^[\d\s+\-*/.(),"']+$/;

    if (!allowedPattern.test(expression)) {
      // If expression contains disallowed characters, return as-is
      return expression;
    }

    // Use Function constructor instead of eval for better security
    // It doesn't have access to local scope variables
    try {
      const func = new Function(`'use strict'; return (${expression});`);
      return func();
    } catch (_error) {
      // If evaluation fails, return the original expression
      return expression;
    }
  }
}
